// ============================================================================
// apps/web/src/pages/LoginPage.tsx
// ----------------------------------------------------------------------------
// Écran de connexion email/password. Flux :
//   1. Saisie email + password
//   2. supabase.auth.signInWithPassword
//   3. Le AuthProvider détecte le changement de session, fetch /api/me
//   4. Redirige selon profile.must_change_password : true → /change-password
//      sinon → / (dashboard)
// ============================================================================

import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { supabase } from '@/lib/supabase';
import { requestPasswordReset } from '@/lib/worker';
import { useAuth } from '@/lib/auth-context';
import { Sparkles, ArrowLeft } from 'lucide-react';

export default function LoginPage() {
  const { status, me } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 'login' = écran de connexion ; 'forgot' = demande de réinitialisation.
  const [mode, setMode] = useState<'login' | 'forgot'>('login');
  const [resetSent, setResetSent] = useState(false);

  if (status === 'authenticated' && me) {
    return <Navigate to={me.profile.must_change_password ? '/change-password' : '/'} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const { error: err } = await supabase.auth.signInWithPassword({ email, password });
    setSubmitting(false);
    if (err) {
      setError(
        err.message === 'Invalid login credentials'
          ? 'Email ou mot de passe incorrect.'
          : err.message,
      );
    }
  }

  async function onForgot(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    // Réponse neutre du serveur (pas d'énumération). Best-effort : on affiche
    // toujours le même message de confirmation, même si l'email est inconnu.
    try {
      await requestPasswordReset(email);
    } catch {
      // On n'expose pas l'échec : le message reste neutre.
    }
    setSubmitting(false);
    setResetSent(true);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-primary/10 p-2.5">
              <Sparkles className="h-7 w-7 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl">Magic</CardTitle>
              <CardDescription>Le Pitchoun &amp; Nulle Pâte Ailleurs</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {mode === 'login' ? (
            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="prenom.nom@pitchoun.fr"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <PasswordInput
                  id="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              {error && (
                <p className="text-sm text-destructive" role="alert">
                  {error}
                </p>
              )}
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? 'Connexion…' : 'Se connecter'}
              </Button>
              <button
                type="button"
                onClick={() => { setMode('forgot'); setError(null); setResetSent(false); }}
                className="w-full text-center text-sm text-muted-foreground hover:text-foreground"
              >
                Mot de passe oublié ?
              </button>
            </form>
          ) : resetSent ? (
            <div className="space-y-4 text-center">
              <p className="text-sm">
                Si un compte existe pour <strong>{email}</strong>, un email avec un
                lien de réinitialisation vient d'être envoyé. Pense à vérifier tes
                spams. Le lien est valable un court instant.
              </p>
              <Button variant="outline" className="w-full" onClick={() => { setMode('login'); setResetSent(false); }}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Retour à la connexion
              </Button>
            </div>
          ) : (
            <form onSubmit={onForgot} className="space-y-4" noValidate>
              <p className="text-sm text-muted-foreground">
                Saisis ton email : on t'envoie un lien pour choisir un nouveau mot de passe.
              </p>
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="prenom.nom@pitchoun.fr"
                />
              </div>
              <Button type="submit" className="w-full" disabled={submitting || !email}>
                {submitting ? 'Envoi…' : 'Envoyer le lien de réinitialisation'}
              </Button>
              <button
                type="button"
                onClick={() => { setMode('login'); setError(null); }}
                className="flex w-full items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4" /> Retour à la connexion
              </button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
